import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';

import { env } from '@/env';
import { getQriotsAccessToken } from '@/lib/logto';
import type {
  LiveGameInfo,
  MultiGameBadgesResult,
  MultiRegionResult,
  Platform,
  ProfileCard,
} from '@/types/qriots';

/**
 * QRiots API client. Talks to qriots-api.
 *
 * Auth is a Logto Bearer JWT with audience `https://qriots.qlash.gg/api`
 * in both dev and prod. The token is minted by getQriotsAccessToken() in
 * src/lib/logto.ts (Task 3.5) via refresh-token grant. The API resource
 * was registered on the Logto PROD tenant on 2026-05-22; see memory
 * reference_qriots_logto_prod_resource.
 *
 * Dev: env.qriotsApiUrl = '/qriots-api', Vite proxy forwards to MacMini.
 * Prod: env.qriotsApiUrl = 'https://qriots.qlash.gg' (direct).
 *
 * No tagTypes — qriots is read-only.
 */
export const qriotsApi = createApi({
  reducerPath: 'qriotsApi',
  baseQuery: retry(
    fetchBaseQuery({
      baseUrl: env.qriotsApiUrl,
      prepareHeaders: async (headers) => {
        const token = await getQriotsAccessToken();
        if (token) headers.set('Authorization', `Bearer ${token}`);
        headers.set('x-platform-id', 'qlash');
        headers.set('Accept', 'application/json');
        return headers;
      },
    }),
    { maxRetries: 1 },
  ),
  keepUnusedDataFor: 600,
  refetchOnFocus: false,
  refetchOnReconnect: true,
  tagTypes: [],
  endpoints: (builder) => ({
    multiRegionLol: builder.query<
      MultiRegionResult,
      { gameName: string; tagLine: string }
    >({
      query: ({ gameName, tagLine }) => ({
        url: '/v1/multi-region/lol',
        params: { gameName, tagLine, platforms: 'default' },
      }),
      keepUnusedDataFor: 3600,
    }),

    multiGameBadges: builder.query<
      MultiGameBadgesResult,
      { gameName: string; tagLine: string; region: Platform }
    >({
      query: ({ gameName, tagLine, region }) => ({
        url: '/v1/multi-game/badges',
        params: { gameName, tagLine, region },
      }),
    }),

    lookupLol: builder.query<
      ProfileCard,
      { gameName: string; tagLine: string; region: Platform }
    >({
      query: ({ gameName, tagLine, region }) => ({
        url: '/v1/lookup/lol',
        params: { gameName, tagLine, region },
      }),
    }),

    liveGameLol: builder.query<
      LiveGameInfo,
      { puuid: string; region: Platform }
    >({
      query: ({ puuid, region }) => ({
        url: `/v1/spectator/lol/${encodeURIComponent(puuid)}`,
        params: { region },
      }),
      // 404 = "not in game" — surfaces as an error in RTK Query; the
      // consuming component (RiotLolDetail) maps it to "no banner" via
      // classifyQriotsError(err).kind === 'not_found'. No retries; the
      // 60s poll provides natural retry.
      extraOptions: { maxRetries: 0 },
    }),
  }),
});

export const {
  useMultiRegionLolQuery,
  useMultiGameBadgesQuery,
  useLookupLolQuery,
  useLiveGameLolQuery,
} = qriotsApi;
